/**
 * Iter XX.221 — Connect Your Cricket client library.
 *
 * Thin, side-effect-free API wrapper. Every endpoint is feature-flagged
 * on the backend — this layer just forwards. Consumers handle the 404
 * (feature not enabled in production) by hiding their surface entirely.
 */

const _base = () => process.env.REACT_APP_BACKEND_URL;

function _authHeader() {
  const t = (typeof window !== "undefined" && window.localStorage)
    ? window.localStorage.getItem("gully_token") : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function _browserId() {
  try {
    return (typeof window !== "undefined" && window.localStorage)
      ? window.localStorage.getItem("gully_browser_id") || null
      : null;
  } catch { return null; }
}

async function _fetch(path, opts = {}) {
  const r = await fetch(`${_base()}/api/cricket${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ..._authHeader(),
      ...(opts.headers || {}),
      ...(_browserId() ? { "X-Browser-Id": _browserId() } : {}),
    },
  });
  if (r.status === 404) return { _disabled: true };
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
  return body;
}

export async function fetchProviders() {
  const r = await _fetch("/connect/providers");
  return r._disabled ? null : r.providers;
}

export async function previewProfile(url) {
  return _fetch("/connect/preview", {
    method: "POST", body: JSON.stringify({ url }),
  });
}

export async function confirmProfile({ url, provider, external_player_id }) {
  return _fetch("/connect/confirm", {
    method: "POST",
    body: JSON.stringify({ url, provider, external_player_id }),
  });
}

export async function dismissConnectPrompt(surface) {
  try {
    return await _fetch("/connect/dismiss", {
      method: "POST", body: JSON.stringify({ surface }),
    });
  } catch { return { ok: false }; }
}

export async function fetchConnection() {
  try {
    const r = await _fetch("/connection");
    return r._disabled ? null : r;
  } catch { return null; }
}

export async function disconnectProfile(profileId) {
  return _fetch("/connection/disconnect", {
    method: "POST", body: JSON.stringify({ profile_id: profileId }),
  });
}

export async function fetchRecordSummary() {
  try {
    const r = await _fetch("/record/summary");
    return r._disabled ? null : r;
  } catch { return null; }
}

/**
 * Simple session-local dismissal so the in-chat card doesn't re-appear
 * for someone who tapped "Maybe later" this session. Server-side
 * dismissal is also recorded via dismissConnectPrompt().
 */
const _DISMISS_KEY = "gully_cyc_dismissed_v1";
export function markSessionDismissed() {
  try { window.sessionStorage.setItem(_DISMISS_KEY, "1"); } catch {}
}
export function isSessionDismissed() {
  try { return window.sessionStorage.getItem(_DISMISS_KEY) === "1"; } catch { return false; }
}
